/**
 * Runtime Extraction Adapter — 3D Model Internals (3D Tiles / BIM / GLB)
 *
 * Builds Tier-2 SceneHierarchyManifest nodes for the internal structure of
 * 3D models: mesh objects, named nodes, sub-tiles, batch-table features.
 * These belong in the ONE Scene Tree per ADR-013 / platform invariants.
 *
 * Detect: layerKind === "3dtiles" OR "bim" (covers IFC-derived, GLB, reality meshes).
 *
 * Falls back gracefully when no sidecar (object-index, model-tree, hierarchy)
 * is available — produces a synthetic root with the model's metadata.
 */

import type { SceneItem } from "@/sdk-stub/gtwin-sdk/types";
import type {
  TreeNode,
  SceneHierarchyManifest,
  TreeNodeDomain,
} from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";
import type {
  DomainAdapter,
  DomainAdapterContext,
  AdapterResult,
} from "../DomainAdapter";

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

function generateUlid(): string {
  let t = Date.now();
  const tsChars: string[] = [];
  for (let i = 0; i < 10; i++) {
    tsChars.unshift(CROCKFORD[t % 32]);
    t = Math.floor(t / 32);
  }
  const randBytes = new Uint8Array(10);
  crypto.getRandomValues(randBytes);
  let rand = "";
  for (let i = 0; i < 10; i++) {
    rand += CROCKFORD[randBytes[i] & 0x1f];
    rand += CROCKFORD[(randBytes[i] >> 5) & 0x07 | ((i < 9 ? randBytes[i + 1] & 0x03 : 0) << 3)];
  }
  return tsChars.join("") + rand.slice(0, 16);
}

function usnMeta(id: string, parentId: string | null) {
  return {
    objectId: id,
    aspects: {
      spatial: { parentId },
      functional: { parentId },
    },
    relationships: [] as const,
  };
}

interface ObjectIndexRecord {
  featureId: string;
  label?: string;
  kind?: string;
  parentId?: string | null;
  metadata?: Record<string, unknown>;
  standardRef?: {
    standard?: string;
    nativeId?: string;
  };
  bounds3d?: { min: number[]; max: number[] };
}

interface ModelTreeNode {
  name: string;
  children?: ModelTreeNode[];
  type?: string;
  metadata?: Record<string, unknown>;
}

const DETECT_KINDS = new Set(["3dtiles", "bim", "ifc-glb", "gltf", "glb"]);

export const ModelInternalsAdapter: DomainAdapter = {
  domain: "building" as TreeNodeDomain,
  version: "2.0.0",
  standard: "OGC:3DTiles+glTF:2.0",

  detect(item: SceneItem): boolean {
    return DETECT_KINDS.has(item.layerKind);
  },

  async buildManifest(
    item: SceneItem,
    asset: { layerId: string; assetNodeId: string; gtwinId: string },
    context: DomainAdapterContext,
  ): Promise<AdapterResult> {
    const nodes: TreeNode[] = [];
    const rootId = generateUlid();

    // `title` is a runtime-only property some scene sources attach; it is not
    // part of the declared SceneItem contract.
    const runtimeTitle = (item as { title?: unknown }).title;
    const modelLabel =
      (typeof runtimeTitle === 'string' ? runtimeTitle : undefined) ??
      asset.assetNodeId ??
      asset.layerId;
    const compression = detectCompressionFromItem(item);
    const sourceCrs = item.sourceCrs ?? null;

    const root: TreeNode = {
      id: rootId,
      parentId: null,
      lor: 3,
      label: modelLabel,
      kind: "asset",
      layerId: asset.layerId,
      featureId: null,
      domain: "building" as TreeNodeDomain,
      standardRef: item.layerKind === "bim"
        ? { standard: "glTF:2.0", classUri: "", nativeId: modelLabel, propertySets: [], linkedDocuments: [] }
        : { standard: "OGC:3DTiles:1.1", classUri: "", nativeId: modelLabel, propertySets: [], linkedDocuments: [] },
      metadata: {
        runtimeExtracted: true,
        source: "model-internals-adapter",
        layerKind: item.layerKind,
        ...(compression.length > 0 ? { compression } : {}),
        ...(sourceCrs ? { sourceCrs } : {}),
      },
      hasChildren: true,
      sortKey: 0,
      ...usnMeta(rootId, null),
    };
    nodes.push(root);

    let enriched = false;
    if (context.fetchJson) {
      const href = item.endpoints["href"]
        ?? item.endpoints["tileset3d"]
        ?? item.endpoints["model"]
        ?? Object.values(item.endpoints)[0]
        ?? "";
      const baseUrl = href.replace(/\/[^/]*$/, "");

      // Try object-index.json (converter sidecar)
      if (!enriched) {
        try {
          const raw = await context.fetchJson(`${baseUrl}/object-index.json`);
          if (raw && typeof raw === "object" && Array.isArray((raw as { records?: unknown }).records)) {
            const records = (raw as { records: ObjectIndexRecord[] }).records;
            buildNodesFromObjectIndex(records, rootId, asset.layerId, nodes);
            enriched = true;
          }
        } catch { /* fallthrough */ }
      }

      // Try model-tree.json (glTF named nodes)
      if (!enriched) {
        try {
          const raw = await context.fetchJson(`${baseUrl}/model-tree.json`);
          if (Array.isArray(raw)) {
            walkModelTree(raw as ModelTreeNode[], rootId, asset.layerId, 0, nodes, 0);
            enriched = true;
          }
        } catch { /* fallthrough */ }
      }

      // Try hierarchy.json (IFC/BIM sidecar)
      if (!enriched) {
        try {
          const raw = await context.fetchJson(`${baseUrl}/hierarchy.json`);
          if (raw && typeof raw === "object") {
            buildNodesFromHierarchy(raw as Record<string, unknown>, rootId, asset.layerId, nodes);
            enriched = true;
          }
        } catch { /* fallthrough */ }
      }
    }

    root.hasChildren = nodes.length > 1;

    const manifest: SceneHierarchyManifest = {
      schema: "geosoft.scene-hierarchy/1",
      gtwinId: asset.gtwinId,
      rootLayerId: asset.layerId,
      generatedAt: new Date().toISOString(),
      domain: "building",
      standardName: item.layerKind === "bim" ? "glTF" : "OGC 3D Tiles",
      standardVersion: item.layerKind === "bim" ? "2.0" : "1.1",
      totalNodes: nodes.length,
      nodeCountByLoR: {},
      nodes,
      paginated: false,
      dataSources: {
        [asset.layerId]: {
          layerId: asset.layerId,
          label: modelLabel,
          mediaKind: item.layerKind,
          primary: true,
        },
      },
    };

    return { ok: true, manifest };
  },
};

function buildNodesFromObjectIndex(
  records: ObjectIndexRecord[],
  rootId: string,
  layerId: string,
  nodes: TreeNode[],
): void {
  const idMap = new Map<string, string>();
  let sortKey = 1;

  for (const rec of records) {
    const nodeId = generateUlid();
    idMap.set(rec.featureId, nodeId);
    const parentNodeId = rec.parentId ? (idMap.get(rec.parentId) ?? rootId) : rootId;

    nodes.push({
      id: nodeId,
      parentId: parentNodeId,
      lor: rec.kind === "group" ? 4 : 6,
      label: rec.label ?? rec.featureId,
      kind: rec.kind === "group" ? "group" : "element",
      layerId,
      featureId: rec.featureId,
      domain: "building" as TreeNodeDomain,
      standardRef: rec.standardRef
        ? {
            standard: rec.standardRef.standard ?? "glTF:2.0",
            classUri: "",
            nativeId: rec.standardRef.nativeId ?? rec.featureId,
            propertySets: [],
            linkedDocuments: [],
          }
        : null,
      metadata: {
        runtimeExtracted: true,
        source: "object-index",
        ...rec.metadata,
        ...(rec.bounds3d ? { bounds3d: rec.bounds3d } : {}),
      },
      hasChildren: false,
      sortKey: sortKey++,
      ...usnMeta(nodeId, parentNodeId),
    });
  }
}

function walkModelTree(
  treeNodes: ModelTreeNode[],
  parentId: string,
  layerId: string,
  depth: number,
  nodes: TreeNode[],
  sortStart: number,
): void {
  let sortKey = sortStart;
  for (const tn of treeNodes) {
    const nodeId = generateUlid();
    const hasChildren = (tn.children?.length ?? 0) > 0;

    nodes.push({
      id: nodeId,
      parentId,
      lor: Math.min(depth + 4, 8),
      label: tn.name || "Unnamed Node",
      kind: hasChildren ? "group" : "element",
      layerId,
      featureId: null,
      domain: "building" as TreeNodeDomain,
      standardRef: {
        standard: "glTF:2.0",
        classUri: "",
        nativeId: tn.name,
        propertySets: [],
        linkedDocuments: [],
      },
      metadata: {
        runtimeExtracted: true,
        source: "model-tree",
        ...(tn.type ? { nodeType: tn.type } : {}),
        ...tn.metadata,
      },
      hasChildren,
      sortKey: sortKey++,
      ...usnMeta(nodeId, parentId),
    });

    if (hasChildren) {
      walkModelTree(tn.children!, nodeId, layerId, depth + 1, nodes, 0);
    }
  }
}

function buildNodesFromHierarchy(
  hierarchy: Record<string, unknown>,
  rootId: string,
  layerId: string,
  nodes: TreeNode[],
): void {
  const elements = hierarchy["elements"] as Array<{
    id?: string;
    name?: string;
    type?: string;
    parentId?: string | null;
    properties?: Record<string, unknown>;
  }> | undefined;

  if (!Array.isArray(elements)) return;

  const idMap = new Map<string, string>();
  let sortKey = 1;

  for (const el of elements) {
    const elId = el.id ?? String(sortKey);
    const nodeId = generateUlid();
    idMap.set(elId, nodeId);
    const parentNodeId = el.parentId ? (idMap.get(el.parentId) ?? rootId) : rootId;

    nodes.push({
      id: nodeId,
      parentId: parentNodeId,
      lor: el.type === "IfcBuildingStorey" ? 4 : 6,
      label: el.name ?? elId,
      kind: el.type?.startsWith("IfcBuilding") ? "group" : "element",
      layerId,
      featureId: elId,
      domain: "building" as TreeNodeDomain,
      standardRef: {
        standard: "IFC:4.3",
        classUri: el.type ?? "",
        nativeId: elId,
        propertySets: [],
        linkedDocuments: [],
      },
      metadata: {
        runtimeExtracted: true,
        source: "hierarchy",
        ifcType: el.type,
        ...el.properties,
      },
      hasChildren: false,
      sortKey: sortKey++,
      ...usnMeta(nodeId, parentNodeId),
    });
  }
}

function detectCompressionFromItem(item: SceneItem): string[] {
  const methods: string[] = [];
  const style = item.style ?? {};
  if (typeof style.compression === "string") {
    const c = String(style.compression).toLowerCase();
    if (c.includes("meshopt")) methods.push("meshopt");
    if (c.includes("draco")) methods.push("draco");
  }
  return methods;
}
